import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Store } from '@ngrx/store';
import { filter } from 'rxjs/operators';
import { EHideMode, IHiddenElementAssignment } from './role-based-element-hide.model';
import { OeeAppState } from '../../store/oee.reducer';
import { componentPermissionActions } from '../../store/component-permission/component-permission.actions';
import { ComponentPermissionService } from './component-permission/component-permission.service';

/**
 * `disable` has no invisibility of its own — it keeps the element on-screen but makes it
 * look and behave like a natively-disabled `scw-mat-input`/`scw-mat-select` (see those
 * components' own disabled-state scss). Since it targets an arbitrary marked element rather
 * than a specific component, the styling rules below target the marked element itself where it
 * IS the relevant native tag (e.g. `role-based-element-hide` placed directly on a raw
 * `<input>`), and also as a descendant of it, since it's usually placed on a wrapper
 * (e.g. `scw-mat-input`/`scw-mat-checkbox`) with the actual native tag nested inside — a
 * `disable` assignment shouldn't have to know which of the two is true for a given element.
 * This CSS only covers the *look* and the mouse (`pointer-events`); true keyboard-level
 * disabling is done by `RoleBasedElementDisableDirective`, which reads the `disable`-mode
 * assignments straight from the `component-permission` store (via `selectDisableSelectors`)
 * and applies the native `inert` attribute — CSS cannot take a native input out of the tab
 * order or stop typing into it.
 */
/** Shared palette for the `disable` look, mirroring a native disabled control. */
const DISABLED_BG_COLOR = '#eff0f0';
const DISABLED_FG_COLOR = '#aeb0b2';
/**
 * `mat-checkbox`'s native `<input type="checkbox">` is kept visually hidden by Material
 * itself (its actual look comes from `.mdc-checkbox__background`/`__checkmark`, driven by the
 * `--_scw-checkbox-*` custom properties in `scw-mat-checkbox.component.scss`). Forcing
 * `opacity`/`background`/`border-color` onto that hidden input — the generic `input` rule
 * below — fights Material's own rendering instead of disabling it, so checkboxes are excluded
 * from that rule and given this dedicated palette (matching that component's own real
 * `:disabled` colors) instead.
 */
const DISABLED_CHECKBOX_COLOR = '#b0b0b0';

const HIDE_MODE_CSS_RULE_BUILDERS: Record<EHideMode, (selectors: string[]) => string> = {
  [EHideMode.hideKeepSpace]: (selectors) => `
    ${selectors.join(', ')} {
      visibility: hidden !important;
      pointer-events: none !important;
      cursor: default !important;
    }
  `,
  [EHideMode.hide]: (selectors) => `
    ${selectors.join(', ')} {
      display: none !important;
    }
  `,
  [EHideMode.disable]: (selectors) => `
    ${selectors.join(', ')} {
      pointer-events: none !important;
      cursor: default !important;
    }

      ${selectors
        .map((selector) => `input${selector}:not([type="checkbox"]), ${selector} input:not([type="checkbox"])`)
        .join(', ')} {
        background: ${DISABLED_BG_COLOR} !important;
        border-color: ${DISABLED_BG_COLOR} !important;
        color: ${DISABLED_FG_COLOR} !important;
        opacity: 1 !important;
      }

      ${selectors.map((selector) => `textarea${selector}, ${selector} textarea`).join(', ')} {
        background: ${DISABLED_BG_COLOR} !important;
        color: ${DISABLED_FG_COLOR} !important;
        opacity: 1 !important;
        border: none !important;
      }

      ${selectors.map((selector) => `a${selector}, ${selector} a`).join(', ')} {
        color: inherit !important;
        text-decoration: none !important;
        cursor: default !important;
        pointer-events: none !important;
      }

      ${selectors.map((selector) => `button${selector}`).join(', ')} {
        background: ${DISABLED_BG_COLOR} !important;
        border-color: ${DISABLED_BG_COLOR} !important;
        color: ${DISABLED_FG_COLOR} !important;
        opacity: 1 !important;
        pointer-events: none !important;
      }

      ${selectors.map((selector) => `${selector} .mdc-checkbox`).join(', ')} {
        --_scw-checkbox-border-color: ${DISABLED_CHECKBOX_COLOR} !important;
        --_scw-checkbox-background: ${DISABLED_CHECKBOX_COLOR} !important;
        --mdc-checkbox-selected-checkmark-color: ${DISABLED_FG_COLOR} !important;
      }

      ${selectors.map((selector) => `${selector} .c-btn, ${selector} .scw-mat-button`).join(', ')} {
        background: ${DISABLED_BG_COLOR} !important;
        border: 1px solid ${DISABLED_BG_COLOR} !important;
        color: ${DISABLED_FG_COLOR} !important;
        opacity: 1 !important;
      }

      ${selectors
        .map((selector) => `${selector} .c-angle-down svg, ${selector} .c-angle-up svg, ${selector} .c-remove svg`)
        .join(', ')} {
        fill: ${DISABLED_FG_COLOR} !important;
      }
  `,
};

@Injectable({
  providedIn: 'root',
})
export class RoleBasedElementHideService {
  private readonly styleElement: HTMLStyleElement;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private readonly store: Store<OeeAppState>,
  ) {
    this.styleElement = this.document.createElement('style');
    this.styleElement.id = 'dynamic-hider-styles';
    this.document.head.appendChild(this.styleElement);
  }

  public initializeHideMechanism(): void {
    this.store
      .select(({ componentPermissionStore }) => componentPermissionStore.assignments)
      .pipe(filter((assignments): assignments is IHiddenElementAssignment[] => assignments !== null))
      .subscribe((assignments) => this.applyHiddenElements(assignments));

    this.store.dispatch(componentPermissionActions.loadCurrentUserAssignments());
  }

  private applyHiddenElements(assignments: IHiddenElementAssignment[]): void {
    if (!assignments?.length) {
      this.styleElement.textContent = '';
      return;
    }

    const selectorsByHideMode = new Map<EHideMode, string[]>();

    assignments.forEach((assignment) => {
      const selector = ComponentPermissionService.resolveElementSelector(assignment.selector);

      if (!selector) {
        // Unknown registry key or an unsafe custom selector — skip this one row rather than
        // letting it throw and take the whole hide mechanism down with it.
        return;
      }

      const selectors = selectorsByHideMode.get(assignment.hideMode) ?? [];
      selectors.push(selector);
      selectorsByHideMode.set(assignment.hideMode, selectors);
    });

    // textContent (not innerHTML): the value carries DB-defined selectors, and setting
    // innerHTML on a <style> element would run the HTML parser over it. textContent writes
    // the CSS verbatim, with no parsing and no way to break out of the <style>.
    this.styleElement.textContent = Array.from(selectorsByHideMode.entries())
      .map(([hideMode, selectors]) => HIDE_MODE_CSS_RULE_BUILDERS[hideMode](selectors))
      .join('\n');
  }
}
